import type { Session } from "@supabase/supabase-js";
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { adminRequest, type UserDetailData } from "../adminApi";
import { Status } from "../components/Status";
import { LoadingPanel } from "../components/LoadingPanel";

export function UserDetailPage({
  mfaToken,
  session,
  setNotice
}: {
  mfaToken: string | null;
  session: Session;
  setNotice(n: { tone: "error" | "success"; text: string } | null): void;
}) {
  const { userId } = useParams<{ userId: string }>();
  const [data, setData] = useState<UserDetailData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    void adminRequest<UserDetailData>(`/users/${userId}`, { mfaToken, session })
      .then(setData)
      .catch((caught) => {
        setNotice({ tone: "error", text: caught instanceof Error ? caught.message : "Could not load user detail." });
      })
      .finally(() => setLoading(false));
  }, [userId]);

  if (loading) return <LoadingPanel label="Loading user detail…" />;
  if (!data) return <p>User not found.</p>;

  return (
    <div className="stack">
      <section className="section">
        <div className="sectionHead">
          <div>
            <Link to="/users">&larr; Users</Link>
            <h1>{data.profile.email}</h1>
            <p>User ID: {data.profile.id}</p>
          </div>
        </div>
      </section>

      <section className="section">
        <h2>Profile</h2>
        <div className="detailGrid">
          <div><span>Role</span><strong>{data.profile.app_role}</strong></div>
          <div><span>Tier</span><strong>{data.profile.tier_id ?? "Default"}</strong></div>
          <div><span>Created</span><strong>{formatDate(data.profile.created_at)}</strong></div>
        </div>
      </section>

      <section className="section">
        <h2>Invitation</h2>
        {data.invitation ? (
          <div className="tableWrap">
            <table>
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Expires</th>
                  <th>Accepted</th>
                  <th>Device</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><Status value={data.invitation.status} /></td>
                  <td>{formatDate(data.invitation.created_at)}</td>
                  <td>{formatDate(data.invitation.expires_at)}</td>
                  <td>{data.invitation.accepted_at ? formatDate(data.invitation.accepted_at) : "—"}</td>
                  <td>{data.invitation.device_label ?? "—"}</td>
                </tr>
              </tbody>
            </table>
          </div>
        ) : <p>No invitation found.</p>}
      </section>

      <section className="section">
        <h2>Vehicles ({data.vehicles.length})</h2>
        {data.vehicles.length > 0 ? (
          <div className="tableWrap">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>VIN</th>
                  <th>Key ready</th>
                  <th>Last seen</th>
                </tr>
              </thead>
              <tbody>
                {data.vehicles.map((v) => (
                  <tr key={v.id}>
                    <td><strong>{v.display_name ?? "Unnamed"}</strong></td>
                    <td>{v.vin}</td>
                    <td><Status value={v.is_key_pair_ready ? "active" : "inactive"} /></td>
                    <td>{v.last_seen_at ? formatDate(v.last_seen_at) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <p>No vehicles linked.</p>}
      </section>

      <section className="section">
        <h2>Workflows ({data.workflows.length})</h2>
        {data.workflows.length > 0 ? (
          <div className="tableWrap">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Steps</th>
                  <th>Enabled</th>
                  <th>Updated</th>
                </tr>
              </thead>
              <tbody>
                {data.workflows.map((w) => (
                  <tr key={w.id}>
                    <td><strong>{w.name}</strong></td>
                    <td>{w.step_count}</td>
                    <td><Status value={w.enabled ? "active" : "inactive"} /></td>
                    <td>{formatDate(w.updated_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <p>No workflows created.</p>}
      </section>

      <section className="section">
        <h2>Recent runs ({data.recent_runs.length})</h2>
        {data.recent_runs.length > 0 ? (
          <div className="tableWrap">
            <table>
              <thead>
                <tr>
                  <th>Workflow</th>
                  <th>Status</th>
                  <th>Trigger</th>
                  <th>Started</th>
                </tr>
              </thead>
              <tbody>
                {data.recent_runs.map((r) => (
                  <tr key={r.id}>
                    <td><strong>{r.workflow_name ?? "Workflow"}</strong></td>
                    <td><Status value={r.status} /></td>
                    <td>{r.trigger_type}</td>
                    <td>{r.created_at ? formatDate(r.created_at) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <p>No runs yet.</p>}
      </section>

      <section className="section">
        <h2>Widget tokens ({data.widget_tokens.length})</h2>
        {data.widget_tokens.length > 0 ? (
          <div className="tableWrap">
            <table>
              <thead>
                <tr>
                  <th>Workflow</th>
                  <th>Token</th>
                  <th>Created</th>
                  <th>Expires</th>
                  <th>Revoked</th>
                </tr>
              </thead>
              <tbody>
                {data.widget_tokens.map((t) => (
                  <tr key={t.id}>
                    <td>{t.workflow_name}</td>
                    <td><code>{t.token_prefix}…</code></td>
                    <td>{formatDate(t.created_at)}</td>
                    <td>{formatDate(t.expires_at)}</td>
                    <td>{t.revoked_at ? formatDate(t.revoked_at) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <p>No widget tokens.</p>}
      </section>

      <section className="section">
        <h2>Audit events ({data.audit_events.length})</h2>
        {data.audit_events.length > 0 ? (
          <div className="tableWrap">
            <table>
              <thead>
                <tr>
                  <th>Event</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {data.audit_events.map((e) => (
                  <tr key={e.id}>
                    <td><code>{e.event_type}</code></td>
                    <td>{formatDate(e.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <p>No audit events.</p>}
      </section>
    </div>
  );
}

function formatDate(value: string) {
  return new Date(value).toLocaleString();
}
